import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Box, Button, IconButton, Typography } from "@mui/material";
import { ChangeEvent, DragEvent, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { radius } from "src/design/tokens";

export interface UploadAreaProps {
  onFile: (file: File) => void;
  accept?: string;
  hint?: string;
  disabled?: boolean;
  /** Called when the chosen file is removed. */
  onClear?: () => void;
  /** `data-testid` of the file input, for the e2e suite; `upload-input` by default. */
  inputTestId?: string;
}

/** Dashed drop zone with a real, labelled file input behind a Browse button. */
export const UploadArea = ({
  onFile,
  accept,
  hint,
  disabled = false,
  onClear,
  inputTestId = "upload-input",
}: UploadAreaProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const hintId = useId();

  const [rejected, setRejected] = useState(false);

  const accepted = (f: File) => {
    if (!accept) return true;
    const name = f.name.toLowerCase();
    return accept
      .split(",")
      .map((a) => a.trim().toLowerCase())
      .filter(Boolean)
      .some((a) =>
        a.startsWith(".")
          ? name.endsWith(a)
          : a.endsWith("/*")
            ? f.type.toLowerCase().startsWith(a.slice(0, -1))
            : f.type.toLowerCase() === a,
      );
  };

  const choose = (f: File | undefined) => {
    if (!f) return;
    if (!accepted(f)) {
      setRejected(true);
      return;
    }
    setRejected(false);
    setFile(f);
    onFile(f);
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    choose(e.target.files?.[0]);
    e.target.value = "";
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    choose(e.dataTransfer.files?.[0]);
  };

  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!disabled) setDragOver(true);
  };

  const clear = () => {
    setFile(null);
    setRejected(false);
    onClear?.();
  };

  return (
    <Box
      data-testid="upload-area"
      className={dragOver ? "drag-over" : undefined}
      onDragOver={onDragOver}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      sx={{
        flex: 1,
        minHeight: 110,
        border: "2px dashed",
        borderColor: dragOver ? "primary.main" : "inputBorder",
        bgcolor: dragOver ? "primary.light" : "surfaceSunken",
        borderRadius: `${radius.panel}px`,
        p: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        gap: 0.75,
        opacity: disabled ? 0.6 : 1,
        transition: "border-color .2s",
        "&:hover": disabled ? undefined : { borderColor: "primary.main" },
      }}
    >
      <UploadFileIcon aria-hidden sx={{ color: "text.secondary" }} />
      <Typography sx={{ fontSize: 15, fontWeight: 800 }}>
        {t("upload.drop", { defaultValue: "Drop a file or browse" })}
      </Typography>
      {hint && (
        <Typography
          id={hintId}
          sx={{ fontSize: 12.5, color: "text.secondary" }}
        >
          {hint}
        </Typography>
      )}
      <input
        ref={inputRef}
        type="file"
        hidden
        tabIndex={-1}
        accept={accept}
        disabled={disabled}
        onChange={onChange}
        data-testid={inputTestId}
        aria-label={t("upload.drop", { defaultValue: "Drop a file or browse" })}
      />
      <Button
        variant="outlined"
        size="small"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        aria-describedby={hint ? hintId : undefined}
      >
        {t("upload.browse", { defaultValue: "Browse" })}
      </Button>
      {rejected && (
        <Typography role="alert" sx={{ fontSize: 13, color: "error.main" }}>
          {t("upload.wrongType", {
            defaultValue: "That file type isn't accepted.",
          })}
        </Typography>
      )}
      {file && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
            {file.name}
          </Typography>
          <IconButton
            size="small"
            disabled={disabled}
            onClick={clear}
            aria-label={t("upload.clear", { defaultValue: "Remove file" })}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default UploadArea;
